import React, { useState, useEffect } from 'react';
import { Package2, TrendingUp, MapPin, Clock, X } from 'lucide-react';
import { Link } from 'react-router-dom';

interface DashboardData {
  activeOrders: number;
  totalAmount: string;
  warehouseItems: number;
  inTransit: number;
  recentOrders: Array<{
    id: number;
    status: string;
    amount: number;
    daysAgo: number;
  }>;
  notifications: string[];
}

function ClientDashboard() {
  const [isLoading, setIsLoading] = useState(true);
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [userName, setUserName] = useState('Клиент');

  useEffect(() => {
    // Get user's first name from localStorage
    const firstName = localStorage.getItem('userFirstName');
    if (firstName) {
      setUserName(firstName);
    }

    const loadDashboardData = async () => {
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Mock data
        const data: DashboardData = {
          activeOrders: 0,
          totalAmount: '¥0',
          warehouseItems: 0,
          inTransit: 0,
          recentOrders: [],
          notifications: []
        };

        setDashboardData(data);
        setIsLoading(false);
        
        // Add slight delay before showing content for smooth transition
        setTimeout(() => {
          setIsContentVisible(true);
        }, 100);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gray-50 flex items-center justify-center z-50">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-[#0171E3]/20 rounded-full"></div>
            <div className="w-20 h-20 border-4 border-[#0171E3] border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
          </div>
          <p className="mt-4 text-gray-600 font-medium">Загрузка данных...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen pt-20 relative transition-opacity duration-500 ${isContentVisible ? 'opacity-100' : 'opacity-0'}`}>
      {/* Mobile Close Button */}
      <Link 
        to="/"
        className="md:hidden fixed top-16 right-4 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white bg-[#161617]/80 rounded-full backdrop-blur-sm z-50"
      >
        <X className="w-5 h-5" />
      </Link>

      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gray-50" style={{
        backgroundImage: `
          linear-gradient(90deg, rgba(249, 250, 251, 0.03) 1px, transparent 1px),
          linear-gradient(0deg, rgba(249, 250, 251, 0.03) 1px, transparent 1px)
        `,
        backgroundSize: '20px 20px',
        backgroundPosition: 'center center'
      }} />

      {/* Content */}
      <div className="relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Мои заказы</h1>
          <p className="mt-1 text-sm text-gray-500">
            Добро пожаловать, {userName}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 animate-fade-in">
          {[
            { label: 'Активные заказы', value: dashboardData?.activeOrders.toString(), icon: Package2, color: 'bg-blue-500' },
            { label: 'Общая сумма', value: dashboardData?.totalAmount, icon: TrendingUp, color: 'bg-green-500' },
            { label: 'На складе', value: dashboardData?.warehouseItems.toString(), icon: MapPin, color: 'bg-yellow-500' },
            { label: 'В пути', value: dashboardData?.inTransit.toString(), icon: Clock, color: 'bg-purple-500' }
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className={`${stat.color} rounded-full p-3`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <div className="text-2xl font-semibold text-gray-900">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fade-in delay-200">
          {/* Orders */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">История заказов</h2>
              <div className="space-y-4">
                {dashboardData?.recentOrders.map((order, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium">Заказ #{order.id}</div>
                      <div className="text-sm text-gray-500">
                        {order.status}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[#0171E3]">¥ {order.amount.toLocaleString()}</div>
                      <div className="text-sm text-gray-500">{`${order.daysAgo} дн. назад`}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Быстрые действия</h2>
              <div className="space-y-4">
                <button
                  onClick={() => setShowRequestForm(true)}
                  className="w-full bg-[#0171E3] text-white py-2 px-4 rounded-lg hover:bg-[#0156aa] transition-colors flex items-center justify-center"
                >
                  Новый заказ
                </button>
                <button className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors">
                  Отследить заказ
                </button>
                <a 
                  href="https://t.me/logistic911"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors text-center block"
                >
                  Поддержка
                </a>
              </div>
            </div>

            {/* Notifications */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Уведомления</h2>
              <div className="space-y-4">
                {dashboardData?.notifications.map((notification, index) => (
                  <div key={index} className="text-sm text-gray-700 pb-2 border-b border-gray-100">
                    {notification}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Request Form Popup */}
      {showRequestForm && (
        <RequestForm 
          isPopup 
          source="Личный кабинет"
          onClose={() => setShowRequestForm(false)} 
        />
      )}
      
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .delay-200 {
          animation-delay: 200ms;
        }

        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
      `}</style>
      </div>
    </div>
  );
}

export default ClientDashboard;